import clsx from 'clsx';
import { memo } from 'react';
import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../constants';
import type { GalleryPhoto } from '../types/gallery';
import { getCaptionText } from '../utils/get-caption-text';
import { Image } from './image';

const thumbnailStyle = {
	width: THUMBNAIL_WIDTH,
	height: THUMBNAIL_HEIGHT,
};

/**
 * Props for a single thumbnail button in the caption strip.
 */
interface ThumbnailProps {
	active?: boolean;
	photo?: GalleryPhoto | null;
	onPress?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	number?: number;
}

/**
 * Renders a clickable thumbnail for selecting a photo by index.
 */
function Thumbnail({
	active = false,
	photo = null,
	onPress,
	number = 0,
}: ThumbnailProps) {
	if (!photo) {
		return null;
	}

	const captionText = getCaptionText(photo.caption);
	const className = clsx(
		'thumbnail-button',
		'gallery-thumbnail-button',
		// Legacy alias kept for 2.x compatibility; use `is-active` going forward.
		active && 'active',
		active && 'is-active',
	);

	return (
		<button
			type="button"
			aria-label={captionText || undefined}
			className={className}
			data-photo-index={number}
			onClick={onPress}
			disabled={false}
		>
			<Image
				alt={photo.thumbnailAlt || photo.alt || captionText}
				src={photo.thumbnail || photo.photo || ''}
				className="thumbnail gallery-thumbnail-image"
				style={thumbnailStyle}
			/>
		</button>
	);
}

const MemoizedThumbnail = memo(Thumbnail);

export { MemoizedThumbnail as Thumbnail };

/**
 * @deprecated Use named import instead: `import { Thumbnail } from './thumbnail'`.
 * Default export will be removed in the next major version.
 */
export default MemoizedThumbnail;
